import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { downloadFile } from '../api';

interface AnswerModel { type: string; scale: number; rating: number | null; text: string | null; items: string[] | null; answered: boolean; respondent_name?: string; }
interface Row { key: string; text: string; self: AnswerModel | null; manager: AnswerModel | null; }
interface OtherQ { key: string; text: string; answer: AnswerModel; }
interface Other { key: string; assignment_id: number; template_name: string; respondent_label: string; respondent_name?: string; questions: OtherQ[]; }
interface Agg { key: string; text: string; type: string; scale: number; answers: AnswerModel[]; count: number; average: number | null; }
interface Model {
  mode: 'employee' | 'manager';
  user: { id: number; full_name: string; org_layer: string; department_name: string | null; manager_name: string | null };
  cycle: { id: number; name: string; year: number };
  self_submitted: boolean;
  above_submitted: boolean;
  above_label: string;
  above_name: string | null;
  self_label: string;
  comparison: Row[];
  aggregated: Agg[];
  named: Other[];
  others: Other[];
  given: (Other & { subject_name: string | null; about_role: string })[];
}

function Answer({ a, missing }: { a: AnswerModel | null; missing: string }) {
  if (!a || !a.answered) return <span className="text-muted text-sm">{missing}</span>;
  return (
    <div>
      {a.rating !== null && <div style={{ fontWeight: 700, color: 'var(--blue-700)' }}>ציון: {a.rating} מתוך {a.scale}</div>}
      {a.text && <div style={{ whiteSpace: 'pre-wrap' }}>{a.text}</div>}
      {a.items && <div>{a.items.length ? a.items.join('  •  ') : 'לא נבחרו אפשרויות'}</div>}
    </div>
  );
}

/** Admin review of one employee's integrated document: choose what appears, add notes, then download. */
export default function AdminDocReview() {
  const { userId, cycleId } = useParams<{ userId: string; cycleId: string }>();
  const navigate = useNavigate();
  const [model, setModel] = useState<Model | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [intro, setIntro] = useState('');
  const [includeGiven, setIncludeGiven] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/exports/employee/${userId}/cycle/${cycleId}/review`)
      .then(({ data }) => {
        setModel(data.model);
        setHidden(new Set<string>(data.review.hidden || []));
        setNotes(data.review.notes || {});
        setIntro(data.review.intro || '');
        setIncludeGiven(!!data.review.include_given);
      })
      .catch(() => navigate('/admin/dashboard'));
  }, [userId, cycleId, navigate]);

  const toggle = (key: string, show: boolean) => {
    setHidden(prev => {
      const next = new Set(prev);
      if (show) next.delete(key); else next.add(key);
      return next;
    });
  };
  const shown = (key: string) => !hidden.has(key);

  const save = async (): Promise<boolean> => {
    setSaving(true); setMsg(''); setError('');
    try {
      await api.put(`/exports/employee/${userId}/cycle/${cycleId}/review`, { hidden: Array.from(hidden), notes, intro, include_given: includeGiven });
      setMsg('הבחירות נשמרו. הן יחולו גם על הורדה מרוכזת של המחלקה.');
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'שגיאה בשמירה');
      return false;
    } finally { setSaving(false); }
  };

  const saveAndDownload = async (anonymous: boolean) => {
    if (await save() && model) {
      await downloadFile(`/exports/employee/${userId}/cycle/${cycleId}?anonymous=${anonymous ? 1 : 0}`, `${model.user.full_name}${anonymous ? '' : ' (עם שמות)'}.docx`);
    }
  };

  if (!model) return <div className="loading"><div className="spinner" />טוען מסמך...</div>;

  const selfMissing = model.self_submitted ? 'לא נענה' : 'טרם הוגש';
  const mgrMissing = model.above_submitted ? 'לא נענה' : 'טרם הוגש';
  let partNo = 0;
  const letters = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳'];
  const part = () => `חלק ${letters[partNo++] || partNo}: `;
  const Check = ({ k, label }: { k: string; label: string }) => (
    <label className="checklist-item" style={{ margin: 0, whiteSpace: 'nowrap' }}>
      <input type="checkbox" checked={shown(k)} onChange={e => toggle(k, e.target.checked)} />
      <span className="text-sm">{label}</span>
    </label>
  );
  const Note = ({ k }: { k: string }) => (
    <input className="form-input" style={{ marginTop: '8px', fontSize: '13px' }} placeholder="הערה שתופיע במסמך מתחת לשאלה (לא חובה)..."
      value={notes[k] || ''} onChange={e => setNotes(p => ({ ...p, [k]: e.target.value }))} />
  );

  return (
    <div className="page">
      <div className="page-header">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/dashboard')}>← חזור ללוח הבקרה</button>
        <h1 style={{ marginTop: '12px' }}>עריכת מסמך משוב: {model.user.full_name}</h1>
        <div className="text-muted text-sm">
          {model.cycle.name}
          {model.user.department_name && <> | מחלקה: {model.user.department_name}</>}
          {model.user.manager_name && <> | מנהל/ת: {model.user.manager_name}</>}
        </div>
        <div className="page-header-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={save} disabled={saving}>{saving ? 'שומר...' : 'שמור בחירות'}</button>
          <button className="btn btn-primary" onClick={() => saveAndDownload(true)} disabled={saving} title="ללא שמות המשיבים – לשיחת המשוב">הורד Word (אנונימי)</button>
          <button className="btn btn-secondary" onClick={() => saveAndDownload(false)} disabled={saving} title="עם שמות המשיבים – לעיון מנהלת הרשות">הורד Word (עם שמות)</button>
        </div>
      </div>

      <div className="alert alert-info">
        סמן/י מה ייכלל במסמך ה-Word. חלקים שאינם מסומנים לא יופיעו בקובץ, וההערות יופיעו מתחת לשאלה. הבחירות נשמרות לכל מחזור בנפרד.
        כאן מוצגים שמות המשיבים לעיונך; בגרסה האנונימית של המסמך הם מוסתרים, ובגרסה "עם שמות" הם מופיעים.
      </div>
      {msg && <div className="alert alert-success">{msg}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><div className="card-title">הקדמה (תופיע בראש המסמך)</div></div>
        <textarea className="form-input" rows={3} value={intro} onChange={e => setIntro(e.target.value)} placeholder="לדוגמה: מסמך זה מסכם את תהליך המשוב לשנת 2026 ומיועד לשיחת המשוב האישית." />
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header"><div className="card-title">{part()}הערכה עצמית מול משוב {model.above_label}</div></div>
        {!model.self_submitted && <div className="alert alert-error" style={{ marginBottom: '8px' }}>ההערכה העצמית טרם הוגשה – צד זה של הטבלה יתמלא לאחר ההגשה.</div>}
        {!model.above_submitted && <div className="alert alert-error" style={{ marginBottom: '8px' }}>משוב {model.above_label}{model.above_name ? ` (${model.above_name})` : ''} טרם הוגש – צד זה של הטבלה יתמלא לאחר ההגשה.</div>}
        {model.comparison.length === 0 && <div className="text-muted">אין עדיין תשובות להצגה.</div>}
        {model.comparison.map(row => (
          <div key={row.key} className="question-block" style={{ opacity: shown(row.key) ? 1 : 0.5 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div className="question-text" style={{ margin: 0 }}>{row.text}</div>
              <Check k={row.key} label="כלול שאלה זו" />
            </div>
            {shown(row.key) && (
              <>
                <div className="grid-2" style={{ marginTop: '10px' }}>
                  <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '10px', opacity: shown(row.key + '|self') ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <strong className="text-sm">{model.self_label}</strong>
                      <Check k={row.key + '|self'} label="הצג" />
                    </div>
                    <Answer a={row.self} missing={!model.self_submitted ? selfMissing : row.self === null ? 'שאלה זו לא נכללה בהערכה העצמית' : selfMissing} />
                  </div>
                  <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '8px', padding: '10px', opacity: shown(row.key + '|manager') ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <strong className="text-sm">{model.above_label}</strong>
                      <Check k={row.key + '|manager'} label="הצג" />
                    </div>
                    <Answer a={row.manager} missing={!model.above_submitted ? mgrMissing : row.manager === null ? `שאלה זו לא נכללה במשוב ${model.above_label}` : mgrMissing} />
                  </div>
                </div>
                <Note k={row.key} />
              </>
            )}
          </div>
        ))}
      </div>

      {model.aggregated.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header"><div className="card-title">{part()}משוב העובדים הישירים (אנונימי, מקובץ לפי שאלה)</div></div>
          {model.aggregated.map(g => (
            <div key={g.key} className="question-block" style={{ opacity: shown(g.key) ? 1 : 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <div className="question-text" style={{ margin: 0 }}>{g.text}</div>
                <Check k={g.key} label="כלול" />
              </div>
              {shown(g.key) && (
                <>
                  {g.average !== null && <div style={{ fontWeight: 700, color: 'var(--blue-700)', marginTop: '6px' }}>ממוצע: {g.average} מתוך {g.scale} ({g.count} משיבים)</div>}
                  {g.answers.filter(a => a.text || a.items || a.rating !== null).map((a, i) => (
                    <div key={i} style={{ marginTop: '6px', paddingRight: '12px', borderRight: '3px solid var(--gray-200)', whiteSpace: 'pre-wrap' }}>
                      <span className="text-muted text-sm">{i + 1}. {a.respondent_name || 'ללא שם'}{a.rating !== null ? ` (ציון ${a.rating})` : ''}: </span>
                      {a.text || (a.items ? a.items.join('  •  ') : '')}
                    </div>
                  ))}
                  {g.count === 0 && <div className="text-muted text-sm">לא התקבלו תשובות</div>}
                  <Note k={g.key} />
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {model.named.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header"><div className="card-title">{part()}משוב המטה והמנהלים (לפי שם)</div></div>
          {model.named.map(o => (
            <div key={o.key} style={{ marginBottom: '16px', opacity: shown(o.key) ? 1 : 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <h3 style={{ margin: 0 }}>{o.respondent_name} <span className="text-muted text-sm">({o.respondent_label}) – {o.template_name}</span></h3>
                <Check k={o.key} label="כלול משוב זה" />
              </div>
              {shown(o.key) && o.questions.map(q => (
                <div key={q.key} className="question-block" style={{ opacity: shown(q.key) ? 1 : 0.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <div className="question-text" style={{ margin: 0 }}>{q.text}</div>
                    <Check k={q.key} label="כלול" />
                  </div>
                  {shown(q.key) && (
                    <>
                      <div style={{ marginTop: '8px' }}><Answer a={q.answer} missing="לא נענה" /></div>
                      <Note k={q.key} />
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {model.others.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header"><div className="card-title">{part()}משובים נוספים</div></div>
          {model.others.map(o => (
            <div key={o.key} style={{ marginBottom: '16px', opacity: shown(o.key) ? 1 : 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <h3 style={{ margin: 0 }}>{o.template_name} <span className="text-muted text-sm">(ממלא/ת: {o.respondent_label})</span></h3>
                <Check k={o.key} label="כלול שאלון זה" />
              </div>
              {shown(o.key) && o.questions.map(q => (
                <div key={q.key} className="question-block" style={{ opacity: shown(q.key) ? 1 : 0.5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <div className="question-text" style={{ margin: 0 }}>{q.text}</div>
                    <Check k={q.key} label="כלול" />
                  </div>
                  {shown(q.key) && (
                    <>
                      <div style={{ marginTop: '8px' }}><Answer a={q.answer} missing="לא נענה" /></div>
                      <Note k={q.key} />
                    </>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {model.given.length > 0 && (
        <div className="card" style={{ marginBottom: '20px', borderColor: 'var(--pink)' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <div className="card-title">משוב ש{model.user.full_name} מילא/ה על אחרים</div>
              <div className="text-muted text-sm">לעיונך בלבד. תשובות אלו מופיעות (במקובץ וללא שם) במסמכים של האנשים שעליהם נכתבו. כברירת מחדל הן אינן נכללות במסמך של {model.user.full_name}.</div>
            </div>
            <label className="checklist-item" style={{ margin: 0, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={includeGiven} onChange={e => setIncludeGiven(e.target.checked)} />
              <span className="text-sm">כלול חלק זה במסמך ה-Word</span>
            </label>
          </div>
          {model.given.map(o => (
            <div key={o.key} style={{ marginBottom: '16px', opacity: shown(o.key) ? 1 : 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <h3 style={{ margin: 0 }}>{o.template_name}{o.subject_name ? <span className="text-muted text-sm"> – עבור: {o.subject_name}</span> : null}</h3>
                {includeGiven && <Check k={o.key} label="כלול" />}
              </div>
              {shown(o.key) && o.questions.map(q => (
                <div key={q.key} className="question-block">
                  <div className="question-text" style={{ margin: 0 }}>{q.text}</div>
                  <div style={{ marginTop: '8px' }}><Answer a={q.answer} missing="לא נענה" /></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button className="btn btn-secondary" onClick={save} disabled={saving}>{saving ? 'שומר...' : 'שמור בחירות'}</button>
        <button className="btn btn-secondary" onClick={() => saveAndDownload(false)} disabled={saving}>הורד Word (עם שמות)</button>
        <button className="btn btn-primary btn-lg" onClick={() => saveAndDownload(true)} disabled={saving}>הורד Word (אנונימי)</button>
      </div>
    </div>
  );
}
